import {
  DEFAULT_NAMESPACE,
  MasonryEngineNode,
  MasonryEngineNodeQueryParams,
} from '@leagueplatform/masonry-engine';
import { deserialise } from 'kitsu-core';
import {
  MasonryNodeResponse,
  MasonrySectionResponseData,
} from '#types/get-masonry-node.ts';
import { getMasonryNode } from '../../utils/get-masonry-node';

type MasonryDeserializeSection = {
  id: MasonrySectionResponseData['id'];
  name: MasonrySectionResponseData['attributes']['name'];
  nodes: { data: Array<MasonryNodeResponse['data']> };
};

export const createHiddenNode = (
  id: MasonryEngineNode['id'],
): MasonryEngineNode => ({
  id,
  type: 'hidden',
  namespace: DEFAULT_NAMESPACE,
  properties: {},
  sections: {},
  actions: {},
});

/**
 *
 * Function formats sections and included key from the API response
 * into sections for a {@link MasonryEngineNode}
 * @param sectionsData after deserializing the JSON:API response
 * @returns
 */
export const formatSectionsToNode = (
  sectionsData: MasonryDeserializeSection[],
) =>
  sectionsData.reduce(
    (
      sections: Record<
        MasonryDeserializeSection['name'],
        Array<MasonryEngineNode>
      >,
      activeSection: MasonryDeserializeSection,
    ) => {
      const sectionsList = sections;
      const {
        name: sectionName,
        nodes: { data: sectionData },
      } = activeSection;
      sectionsList[sectionName] = sectionData.map(
        (nodeInSection: MasonryNodeResponse['data']) =>
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          formatMasonryNode({
            data: nodeInSection,
            included: [],
          }),
      );
      return sectionsList;
    },
    {},
  );

export type MasonryRestGetterParams = {
  id: MasonryEngineNode['id'];
  pathname: string;
  method: Request['method'];
};

/**
 * Function to format Masonry Engine API response to {@link MasonryEngineNode}
 * @param data - Response data from API
 * @returns {@link MasonryEngineNode}
 */
export const formatMasonryNode = (data: MasonryNodeResponse) => {
  const formattedData = deserialise(data);

  const getNodeMeta = data.data.meta?.getNode;

  const {
    node: { actions, properties, states, type, namespace, themeConfiguration },
    sections,
    id,
  } = formattedData.data;

  const nodeValue: MasonryEngineNode = {
    id,
    type,
    properties: properties || {},
    namespace: namespace || DEFAULT_NAMESPACE,
    actions: actions || {},
    states,
    themeConfiguration,
    sections: sections ? formatSectionsToNode(sections.data) : {},
  };

  if (getNodeMeta) {
    const { pathname, searchParams: initialQueryParams } = new URL(
      getNodeMeta.href,
      window.location.origin,
    );

    nodeValue.asyncConfig = {
      getterParams: {
        id,
        pathname,
        method: getNodeMeta.method,
      },
      initialQueryParams: Object.fromEntries(initialQueryParams.entries()),
    };
  }

  return nodeValue;
};

export const getAsyncNodeData = async (
  getterParams: MasonryRestGetterParams,
  queryParams?: MasonryEngineNodeQueryParams,
): Promise<MasonryEngineNode> => {
  const { id, pathname, method } = getterParams;
  const queryString = queryParams
    ? new URLSearchParams(queryParams).toString()
    : '';
  const pathWithParams = queryString ? `${pathname}?${queryString}` : pathname;

  const data = await getMasonryNode({
    path: pathWithParams,
    method,
  });

  if (!data) {
    return createHiddenNode(id);
  }

  return formatMasonryNode(data);
};
